/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState, useRef, useEffect } from "react";
import { FlexGridCellTemplate } from "@grapecity/wijmo.react.grid";
import DataGridColumn from "./DataGridColumn";
import UnitConversionDisplay from "../presentation/UnitConversionDisplay";
import useUnitConversion from "../../../hooks/useUnitConversion";
import { getPurposes } from "../../../utils/unitConversionUtils";
import UnitPurposeSelectDialog from "../dialog/UnitPurposeSelectDialog";

/**
 * @COMPONENT_NAME DataGridUnitConversionColumn
 * @DESCRIPTION 단위 변환 기능이 포함된 데이터 그리드 컬럼 컴포넌트
 *              그리드 셀에 단위 변환 표시 기능을 제공하고 셀 클릭 시 단위 용도 선택 다이얼로그를 열어
 *              선택된 용도에 따라 적절한 단위로 변환된 값을 표시
 * @PROPS
 *   - {Object} schema - 컬럼 스키마 정보 (name, header 등)
 *   - {Object} others - 기타 DataGridColumn에 전달될 props (width, align 등)
 *
 * @STATE
 *   - {Object} selectedPurpose - 현재 선택된 단위 용도 객체 (unitPurposeId, purposeName 포함)
 *   - {Object} dialogRef - 단위 용도 선택 다이얼로그 인스턴스 참조
 *   - {Object} purposes - 단위 용도 리스트 참조
 *
 * @CALLBACKS
 *   - {Function} handleCellClick - 셀 클릭 시 단위 목적 선택 다이얼로그를 표시하는 콜백
 *   - {Function} handleDialogConfirm - 다이얼로그에서 목적 선택 완료 시 호출되는 콜백
 */
const DataGridUnitConversionColumn = ({ schema, ...others }) => {
    const { unitPurposeList, unitGroupDetailList } = useUnitConversion();

    const dialogRef = useRef(null);
    const purposes = useRef(null);

    const [selectedPurpose, setSelectedPurpose] = useState(null);

    useEffect(() => {
        purposes.current = getPurposes(unitPurposeList);
        setSelectedPurpose(purposes.current[0]);
    }, [unitPurposeList]);

    const handleCellClick = () => {
        dialogRef.current?.show();
    };

    const handleDialogConfirm = (purpose) => {
        setSelectedPurpose(purpose);
        dialogRef.current?.hide();
    };

    const cellTemplate = useMemo(
        () => (ctx) => {
            return (
                <div style={{ width: "100%", height: "100%" }} onClick={handleCellClick}>
                    <UnitConversionDisplay
                        ctx={ctx}
                        schema={schema}
                        selectedPurpose={selectedPurpose}
                        unitGroupDetailList={unitGroupDetailList}
                        unitPurposeList={unitPurposeList}
                    />
                </div>
            );
        },
        [unitGroupDetailList, unitPurposeList, selectedPurpose, schema]
    );

    return (
        <>
            <DataGridColumn schema={schema} {...others}>
                <FlexGridCellTemplate
                    key={selectedPurpose?.unitPurposeId || "default"}
                    cellType="Cell"
                    template={cellTemplate}
                />
            </DataGridColumn>
            <UnitPurposeSelectDialog
                onInitialized={(instance) => (dialogRef.current = instance)}
                onConfirm={handleDialogConfirm}
                purposeList={purposes.current}
                currentPurpose={selectedPurpose}
            />
        </>
    );
};

export default DataGridUnitConversionColumn;
